import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { compressFilesToWebP } from '@/lib/imageUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Download, Loader2, X } from 'lucide-react';
import type { ApiError, UpdateAssetInput } from '@pms/shared';
import { updateAssetSchema } from '@pms/shared';
import {
  assetsService,
  type AssetCategory,
  type AssetDetail,
  type AssetDocument,
  type AssetStatus,
  type AssetSubcategory,
} from '@/services/assets.service';
import { FileDropZone } from './FileDropZone';
import { AuthImage } from '@/components/ui/auth-image';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const NONE = '__none__';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface Props {
  open: boolean;
  onClose: () => void;
  asset: AssetDetail | null;
  statuses: AssetStatus[];
  categories: AssetCategory[];
}

export function EditAssetModal({ open, onClose, asset, statuses, categories }: Props) {
  const queryClient = useQueryClient();
  const [subcats, setSubcats] = useState<AssetSubcategory[]>([]);
  const [newInvoiceFiles, setNewInvoiceFiles] = useState<File[]>([]);
  const [newPhotoFiles, setNewPhotoFiles] = useState<File[]>([]);
  const [deletedDocIds, setDeletedDocIds] = useState<string[]>([]);

  const existingInvoices = (asset?.documents ?? []).filter(
    (d) => d.document_type === 'invoice' && !deletedDocIds.includes(d.id),
  );
  const existingPhotos = (asset?.documents ?? []).filter(
    (d) => d.document_type === 'photo' && !deletedDocIds.includes(d.id),
  );

  const removeExisting = (doc: AssetDocument) =>
    setDeletedDocIds((prev) => [...prev, doc.id]);

  const { data: brandsData } = useQuery({
    queryKey: ['assets', 'brands'],
    queryFn: () => assetsService.listBrands(),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users', 'active-all'],
    queryFn: () => usersService.listAllActive(),
  });

  const { data: deptsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  // Active staff, plus whoever currently holds the asset. That holder may since have
  // been deactivated, and dropping them from the options would make the Select show a
  // blank value and quietly clear the assignment on the next save.
  const assignOptions = (() => {
    const opts = (usersData ?? []).map(u => ({
      id: u.id,
      label: `${u.first_name} ${u.last_name}`.trim(),
    }));
    const current = asset?.assigned_to;
    if (current && !opts.some(o => o.id === current.id)) {
      opts.push({ id: current.id, label: current.name });
    }
    return opts;
  })();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UpdateAssetInput>({
    resolver: zodResolver(updateAssetSchema),
  });

  const watchCategoryId = watch('category_id');
  const watchWarranty = watch('warranty_available');

  // Load subcategories when category changes
  useEffect(() => {
    const catId = watchCategoryId;
    if (catId && catId !== NONE) {
      assetsService.listSubcategories(catId).then(setSubcats).catch(() => setSubcats([]));
    } else {
      setSubcats([]);
    }
  }, [watchCategoryId]);

  // Pre-fill form when asset changes
  useEffect(() => {
    if (asset) {
      reset({
        category_id: asset.category?.id ?? '',
        subcategory_id: asset.subcategory?.id ?? null,
        brand_id: asset.brand?.id ?? null,
        model_name: asset.model_name ?? null,
        purchase_date: asset.purchase_date ?? null,
        warranty_available: asset.warranty_available,
        warranty_end_date: asset.warranty_end_date ?? null,
        status_id: asset.status?.id ?? '',
        assigned_to_user_id: asset.assigned_to?.id ?? null,
        assigned_department_id: asset.department?.id ?? null,
        notes: asset.notes ?? null,
      });
      // Load subcategories for the pre-filled category
      if (asset.category?.id) {
        assetsService
          .listSubcategories(asset.category.id)
          .then(setSubcats)
          .catch(() => setSubcats([]));
      }
    }
  }, [asset, reset]);

  const mutation = useMutation({
    mutationFn: async (data: UpdateAssetInput) => {
      await assetsService.updateAsset(asset!.id, data);
      await Promise.all(deletedDocIds.map((id) => assetsService.deleteDocument(id)));
      const processedInvoices = await compressFilesToWebP(newInvoiceFiles);
      const processedPhotos   = await compressFilesToWebP(newPhotoFiles);
      await Promise.all(processedInvoices.map((f) => assetsService.uploadDocument(asset!.id, f, 'invoice')));
      await Promise.all(processedPhotos.map((f) => assetsService.uploadDocument(asset!.id, f, 'photo')));
    },
    onSuccess: () => {
      toast.success('Asset updated successfully');
      void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['assets', 'detail', asset?.id] });
      setNewInvoiceFiles([]);
      setNewPhotoFiles([]);
      setDeletedDocIds([]);
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const onSubmit = (data: UpdateAssetInput) => {
    const cleaned: UpdateAssetInput = {
      ...data,
      subcategory_id: data.subcategory_id || null,
      brand_id: data.brand_id || null,
      model_name: data.model_name || null,
      purchase_date: data.purchase_date || null,
      warranty_end_date: data.warranty_available ? data.warranty_end_date : null,
      assigned_to_user_id: data.assigned_to_user_id || null,
      assigned_department_id: data.assigned_department_id || null,
      notes: data.notes || null,
    };
    mutation.mutate(cleaned);
  };

  const handleClose = () => {
    reset();
    setNewInvoiceFiles([]);
    setNewPhotoFiles([]);
    setDeletedDocIds([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Edit Asset{asset ? ` — ${asset.asset_code}` : ''}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Category */}
            <div className="space-y-1">
              <Label>Category <span className="text-destructive">*</span></Label>
              <Controller
                control={control}
                name="category_id"
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger className={errors.category_id ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.category_id && (
                <p className="text-xs text-destructive">{errors.category_id.message}</p>
              )}
            </div>

            {/* Sub Category */}
            <div className="space-y-1">
              <Label>Sub Category</Label>
              <Controller
                control={control}
                name="subcategory_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                    disabled={subcats.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {subcats.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Brand */}
            <div className="space-y-1">
              <Label>Brand</Label>
              <Controller
                control={control}
                name="brand_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {(brandsData ?? []).map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Model Name */}
            <div className="space-y-1">
              <Label>Model Name</Label>
              <Input {...register('model_name')} placeholder="e.g. ThinkPad X1 Carbon" />
            </div>

            {/* Purchase Date */}
            <div className="space-y-1">
              <Label>Purchase Date</Label>
              <Controller
                control={control}
                name="purchase_date"
                render={({ field }) => (
                  <DatePicker value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
                )}
              />
            </div>

            {/* Status */}
            <div className="space-y-1">
              <Label>Status <span className="text-destructive">*</span></Label>
              <Controller
                control={control}
                name="status_id"
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger className={errors.status_id ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: s.color }}
                            />
                            {s.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.status_id && (
                <p className="text-xs text-destructive">{errors.status_id.message}</p>
              )}
            </div>

            {/* Assigned To */}
            <div className="space-y-1">
              <Label>Assigned To</Label>
              <Controller
                control={control}
                name="assigned_to_user_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Unassigned</SelectItem>
                      {assignOptions.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Department */}
            <div className="space-y-1">
              <Label>Department</Label>
              <Controller
                control={control}
                name="assigned_department_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {(deptsData?.data ?? []).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* Warranty toggle */}
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="warranty_available"
              render={({ field }) => (
                <input
                  type="checkbox"
                  id="edit_warranty_available"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
                />
              )}
            />
            <Label htmlFor="edit_warranty_available" className="cursor-pointer">
              Warranty Available
            </Label>
          </div>

          {watchWarranty && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Warranty End Date</Label>
                <Controller
                  control={control}
                  name="warranty_end_date"
                  render={({ field }) => (
                    <DatePicker value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
                  )}
                />
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              {...register('notes')}
              rows={3}
              placeholder="Additional notes about this asset..."
            />
          </div>

          {/* Invoice / Bill */}
          <div className="space-y-1">
            <Label>Invoice / Bill</Label>
            {existingInvoices.length > 0 && (
              <ul className="mb-2 space-y-1">
                {existingInvoices.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex items-center justify-between rounded-md border border-border/60 bg-muted/20 px-3 py-1.5"
                  >
                    <button
                      type="button"
                      onClick={() => assetsService.downloadDocument(doc.id, doc.original_name)}
                      className="truncate text-xs text-primary hover:underline mr-2 flex items-center gap-1"
                    >
                      <Download className="h-3 w-3 shrink-0" />
                      {doc.original_name}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeExisting(doc)}
                      className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <FileDropZone
              files={newInvoiceFiles}
              onFilesChange={setNewInvoiceFiles}
              accept=".pdf,.jpg,.jpeg,.png"
              placeholder="Click to upload invoice / bill"
            />
          </div>

          {/* Asset Photos */}
          <div className="space-y-1">
            <Label>Asset Photos</Label>
            {existingPhotos.length > 0 && (
              <div className="mb-2 grid grid-cols-3 gap-2">
                {existingPhotos.map((doc) => (
                  <div key={doc.id} className="relative group">
                    <AuthImage
                      src={`/assets/documents/${doc.id}/file`}
                      alt={doc.original_name}
                      className="h-20 w-full rounded object-cover border border-border/60"
                    />
                    <button
                      type="button"
                      onClick={() => removeExisting(doc)}
                      className="absolute top-1 right-1 rounded-full bg-black/50 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <FileDropZone
              files={newPhotoFiles}
              onFilesChange={setNewPhotoFiles}
              accept="image/*"
              multiple
              placeholder="Click to upload photos (multiple allowed)"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting || mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {(isSubmitting || mutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
